<template lang='pug'>
  .horizontal-selector(v-if="isMounted" :class="{'left-overflow': leftOverflow, 'right-overflow': rightOverflow, 'dragging': dragging}")
    touch.inner-wrap(ref="innerWrap" @left="swipeLeft" @right="swipeRight" @tstart="startDrag" @tmove="drag" @tend="endDrag"  :scroll-takeover-direction="'horizontal'"  :style="{width: innerWidth + 'px', 'margin-left': adjustedMargin + 'px'}")
      slot
</template>

<script>
  import touch from './touch';

  export default {
    name: "HorizontalSelector",
    emits: ['swipe-left', 'swipe-right', 'overflow'],

    components: {
      touch,
    },

    data() {
      return {
        isMounted: false,
        changeWatcher: null,
        outerWidth: 0,
        innerWidth: 0,
        numChildren: 0,
        dragging: false,
        dragX: 0
      };
    },

    props: {
      selectedIndex: {
        type: Number
      },
      dragMode: {
        type: Boolean,
        default: false
      },
      centerMode: {
        type: Boolean,
        default: false
      },
      centerWidth: {
        type: Number,
        default: 1
      }
    },

    computed: {
      children() {
        if (this.$refs.innerWrap && this.$refs.innerWrap.$el && this.$refs.innerWrap.$el.children) {
          return this.$refs.innerWrap.$el.children;
        } else {
          return [];
        }
      },

      adjustedMargin() {
        var offset = 0;
        var maxOffset = this.innerWidth > this.outerWidth ? this.innerWidth - this.outerWidth + 1 : 0;

        if (!this.centerMode && this.isMounted && this.innerWidth && this.outerWidth && this.numChildren && this.selectedIndex > 1 && this.children[this.selectedIndex]) {
          if (this.innerWidth > this.outerWidth) {
            for (let x = 0; x < this.selectedIndex - 2; x++) {
              offset += this.children[x].offsetWidth;
            }

            offset = offset + (this.children[this.selectedIndex - 2].offsetWidth / 1.5);
            offset = offset * -1;
          }
        } else if (this.centerMode && this.isMounted && this.innerWidth && this.outerWidth && this.numChildren && this.children[this.selectedIndex]) {
          for (let x = 0; x < this.selectedIndex; x++) {
            offset += this.children[x].offsetWidth;
          }

          offset += this.children[this.selectedIndex].offsetWidth / 2;

          if (this.centerWidth > 1) {
            for (let x = 1; x < this.centerWidth; x++) {
              if (this.children[this.selectedIndex + x]) {
                offset += this.children[this.selectedIndex + x].offsetWidth / 2;
              } else if (this.children[this.selectedIndex - 1]) {
                offset -= this.children[this.selectedIndex -1].offsetWidth / 2;
              }
            }
          }

          offset = offset * -1;
          offset = offset + (this.outerWidth / 2);

          if (this.dragMode && this.dragging) {
            offset += this.dragX;
          }

          return offset;
        }

        if (this.dragMode && this.dragging) {
          offset += this.dragX;
        }

        if (offset > 0) {
          return 0;
        }

        if (-1 * offset > maxOffset) {
          return -1 * maxOffset;
        }

        return offset;
      },

      leftOverflow() {
        return this.adjustedMargin < 0;
      },

      rightOverflow() {
        return this.innerWidth + this.adjustedMargin > this.outerWidth;
      },

      hasOverflow() {
        return this.innerWidth > this.outerWidth;
      }
    },

    watch: {
      hasOverflow: {
        immediate: true,
        handler() {
          this.$emit('overflow', this.hasOverflow);
        }
      }
    },

    methods: {
      swipeLeft(distance) {
        this.$emit('swipe-left', this.distanceInChildren('left', distance));
      },

      swipeRight(distance) {
        this.$emit('swipe-right', this.distanceInChildren('right', distance));
      },

      distanceInChildren(direction, distance) {
        if (this.numChildren == 0 || (direction == 'right' && this.selectedIndex == 0) || (direction == 'left' && this.selectedIndex == this.numChildren - 1)) {
          return 0;
        }

        var childrenScrolled = 0;
        
        if (this.children[this.selectedIndex]) {
          var remainingDistance = distance - (this.children[this.selectedIndex].offsetWidth / 2);
          
          if (remainingDistance < 0) {
            remainingDistance = 1;
          }

          var increment = direction == 'right' ? -1 : 1;
          var childIndex = this.selectedIndex + increment;

          while (remainingDistance > 0 && this.children[childIndex]) {
            var childWidth = this.children[childIndex].offsetWidth;
            
            if (remainingDistance < childWidth) {
              childrenScrolled += remainingDistance / childWidth;
              remainingDistance = 0;
            } else {
              childrenScrolled += 1;
              remainingDistance += -childWidth;
            }

            childIndex += increment;
          }
        }

        return childrenScrolled;
      },

      startDrag() {
        this.dragging = true;
      },

      drag({x}) { // tmove emits an object { x, y }
        this.dragX = x;
      },

      endDrag() {
        this.dragX = 0;
        this.dragging = false;
      },

      getInnerWidth() {
        if (!this.isMounted) {
          return 0;
        }
        var innerWidth = 0;
        const innerWrapEl = this.$refs.innerWrap?.$el || this.$refs.innerWrap;

        if (innerWrapEl && innerWrapEl.children) {
          for (let x = 0; x < innerWrapEl.children.length; x++) {
            innerWidth += innerWrapEl.children[x].offsetWidth;
          }
        }

        innerWidth = Math.ceil(innerWidth + 1);

        return innerWidth;
      }
    },

    mounted() {
      this.isMounted = true;
      this.changeWatcher = setInterval(() => {
        this.outerWidth = this.$el.offsetWidth;
        this.innerWidth = this.getInnerWidth();
        const innerWrapEl = this.$refs.innerWrap?.$el || this.$refs.innerWrap;
        this.numChildren = innerWrapEl?.children?.length || 0;
      }, 300);
    },

    unmounted() {
      clearInterval(this.changeWatcher);
    }
  };
</script>

<style scoped lang="stylus">
  .horizontal-selector
    width 100%

    .inner-wrap
      white-space nowrap
      transition(margin 350ms ease-in-out)

    &.dragging
      .inner-wrap
        transition(none)

</style>
